
// DeviceContext.js
//
// Provides interaction-model awareness to the entire app: are we on a
// touch-primary device, and roughly how much room does it have. This is
// deliberately narrow — it exists to answer "which Header should mount"
// (a genuine structural fork; a hamburger drawer and a hover-driven nav
// bar are different DOM/interaction models, not the same thing restyled).
//
// It used to classify devices by parsing navigator.userAgent, with a
// window-width fallback for "ambiguous" agents. Two problems with that:
//
//   1. It was actually broken: iPadOS has sent a desktop Safari UA string
//      since iOS 13 (no "iPad", no "Mobile" token), so real iPads fell
//      through to the width fallback — and in landscape (~1194px) that
//      fallback also failed, silently handing them the full desktop
//      mouse-hover header on a touchscreen with no mouse.
//   2. UA sniffing degrades over time by design — browsers keep freezing/
//      reducing UA detail for privacy, so this class of regex gets less
//      reliable, not more.
//
// What we actually want to know ("does this device primarily use touch,
// and does it support hover") has direct feature-detection support:
// `pointer: coarse|fine` and `hover: none|hover`. So we ask that instead
// of trying to guess it from a spoofable/shrinking string.
//
// `width` and `desktopLogoVariant` used to live here too, which meant the
// *entire app* re-rendered on every resize-pixel (width was updated
// unconditionally in a `resize` listener) just to redraw one logo inside
// one component. Both are gone — `useMediaQuery` (matchMedia + change
// events) only notifies on an actual threshold crossing, and the logo
// breakpoint now lives locally in HeaderDesktop.js, the only place that
// cares about it.
//
// Usage:
//   import { useDevice } from '../context/DeviceContext';
//   const { isMobile, isTablet, isDesktop, deviceType } = useDevice();

import React, { createContext, useContext } from 'react';

import { useMediaQuery } from '../hooks/useMediaQuery';

// ─── Breakpoints ────────────────────────────────────────────────────────────
const MOBILE_MAX_WIDTH = 767;  // <= : mobile-sized viewport
const TABLET_MAX_WIDTH = 1023; // <= : tablet-sized viewport (and > mobile)

// ─── Context ──────────────────────────────────────────────────────────────────

const DeviceContext = createContext({
    deviceType: 'desktop',
    isMobile:   false,
    isTablet:   false,
    isDesktop:  true,
});

// ─── Provider ─────────────────────────────────────────────────────────────────

export const DeviceProvider = ({ children }) => {
    const isCoarsePointer = useMediaQuery('(pointer: coarse)'); // touch-primary input
    const isNarrow = useMediaQuery(`(max-width: ${MOBILE_MAX_WIDTH}px)`);
    const isMedium = useMediaQuery(`(max-width: ${TABLET_MAX_WIDTH}px)`);

    // A device with a mouse/trackpad (fine pointer) always gets the desktop
    // header, regardless of width — that's the correct interaction model
    // for hover-driven nav. Only coarse-pointer (touch-primary) devices are
    // routed to mobile/tablet, and only a wide touch device (e.g. landscape
    // iPad Pro, a touch laptop) falls back to desktop.
    const deviceType = !isCoarsePointer ? 'desktop'
        : isNarrow ? 'mobile'
        : isMedium ? 'tablet'
        : 'desktop';

    const value = {
        deviceType,
        isMobile:  deviceType === 'mobile',
        isTablet:  deviceType === 'tablet',
        isDesktop: deviceType === 'desktop',
    };

    return (
        <DeviceContext.Provider value={value}>
            {children}
        </DeviceContext.Provider>
    );
};

// ─── Hook ─────────────────────────────────────────────────────────────────────

/**
 * Returns the current device context.
 * Must be used inside a <DeviceProvider>.
 *
 * @returns {{ deviceType: string, isMobile: boolean, isTablet: boolean, isDesktop: boolean }}
 */
export const useDevice = () => useContext(DeviceContext);

export default DeviceContext;
